/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';

import ActionListGrid from '@broadleaf/admin-components/dist/collection/components/ActionListGrid';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import ActionRegistrar from '@broadleaf/admin-components/dist/metadata/services/ActionRegistrar';
import useInvoicesGrid from '@src/oms/InvoicesView/hooks/useInvoicesGrid/index';
import { getFetchInvoicesEndpoint } from '@src/oms/InvoicesView/utils/InvoicesOperationUtils';

import messages from '@src/oms/InvoicesView/utils/InvoicesGridUtils.messages';
import type { ICommonGridHeader } from '@broadleaf/admin-components/dist/types/common';
import { getDefaultInvoiceHeaders } from '@src/oms/InvoicesView/utils/InvoicesGridUtils';
import { InvoiceGridDefinition } from '@src/oms/InvoicesView/utils/types';
import AdvancedSearchQueryBuilder from '@broadleaf/admin-components/dist/collection/components/AdvancedSearchQueryBuilder';
import SubmitInvoicesAction from '@src/oms/InvoicesView/components/action-components/SubmitInvoicesAction';
import GetInvoiceDetailsAction from '@src/oms/InvoicesView/components/action-components/GetInvoiceDetailsAction';
import ActionListGridQueryFilter from '@broadleaf/admin-components/dist/collection/components/ActionListGrid/action-components/ActionListGridQueryFilter';
import { MarkInvoicesPaidAction } from '@src/oms/InvoicesView/components/action-components/MarkInvoicesPaidAction';
import GetInvoicePdfAction from '@src/oms/InvoicesView/components/action-components/GetInvoicePdfAction/GetInvoicePdfAction';
import InvoicesExportAction from '@src/oms/InvoicesView/components/action-components/InvoicesExportAction/InvoicesExportAction';
import { useLabelWithTotals } from '@src/oms/InvoicesView/hooks/useLabelWithTotals';

export const HeldInvoicesGrid: InvoiceGridDefinition = () => {
  const { collectionState, getActionListGridProps } = useInvoicesGrid({
    readConfig: getFetchInvoicesEndpoint(),
    //use this grid to show invoices that are held for review or have a credit pending
    invoiceStatuses: ['HOLD_FOR_REVIEW', 'REQUIRES_PO', 'CREDIT_PENDING']
  });

  const actionListGridProps = getActionListGridProps();
  const actionComponents = ActionRegistrar.getActionComponents(
    'View',
    HeldInvoicesGrid.VIEW_TYPE
  );
  const actionDefinitions = useHeldInvoicesActionDefinitions();
  const headers = useInvoiceHistoryHeaders();
  const label = useLabelWithTotals(collectionState, messages.heldInvoices);
  return (
    <ActionListGrid
      augmentationKey={''}
      {...actionListGridProps}
      actionComponents={actionComponents}
      actionDefinitions={actionDefinitions}
      headers={headers}
      label={label}
      sortable={true}
    />
  );
};

HeldInvoicesGrid.VIEW_TYPE = 'HELD_INVOICES_GRID';
HeldInvoicesGrid.actionComponents = [
  {
    component: AdvancedSearchQueryBuilder,
    placement: 'ADVANCED_FILTER',
    type: 'QUERY_BUILDER'
  },
  {
    component: SubmitInvoicesAction,
    placement: 'GRID',
    type: 'SUBMIT'
  },
  {
    component: MarkInvoicesPaidAction,
    placement: 'GRID',
    type: 'MARK_PAID'
  },
  {
    component: InvoicesExportAction,
    placement: 'GRID',
    type: 'INVOICES_EXPORT'
  },
  {
    component: GetInvoiceDetailsAction,
    placement: 'ROW',
    type: 'VIEW_DETAILS'
  },
  {
    component: GetInvoicePdfAction,
    placement: 'ROW',
    type: 'DOWNLOAD_ORDER_INVOICE'
  },
  {
    component: ActionListGridQueryFilter,
    placement: 'QUERY_FILTER',
    type: 'QUERY'
  }
];

const useInvoiceHistoryHeaders = (): Array<ICommonGridHeader> => {
  const formatMessage = useFormatMessage();
  return [...getDefaultInvoiceHeaders(formatMessage)];
};

const useHeldInvoicesActionDefinitions = () => {
  const formatMessage = useFormatMessage();
  return [
    {
      label: formatMessage(messages.gridActions.submit),
      placement: 'GRID',
      type: 'SUBMIT'
    },
    {
      label: formatMessage(messages.gridActions.markPaid),
      placement: 'GRID',
      type: 'MARK_PAID'
    },
    {
      label: formatMessage(messages.gridActions.bulkExport),
      placement: 'GRID',
      type: 'INVOICES_EXPORT'
    },
    {
      label: formatMessage(messages.gridActions.viewDetails),
      placement: 'ROW',
      type: 'VIEW_DETAILS'
    },
    {
      label: formatMessage(messages.gridActions.downloadOrderInvoice),
      placement: 'ROW',
      type: 'DOWNLOAD_ORDER_INVOICE'
    },
    {
      attributes: {
        filterKey: 'q'
      },
      label: 'Filter by Name...',
      placement: 'QUERY_FILTER',
      type: 'QUERY'
    },
    {
      attributes: {
        filterKey: 'query_builder'
      },
      label: formatMessage(messages.gridActions.criteriaQuery),
      placement: 'ADVANCED_FILTER',
      type: 'QUERY_BUILDER'
    }
  ];
};

export default HeldInvoicesGrid;
